import React from "react";
import axios from "axios";
import { toast } from "sonner";
import SiteLayout from "@/components/SiteLayout";
import { Mail, Phone, MapPin, ArrowRight } from "lucide-react";
import { EVARA } from "@/data/evara";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = React.useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = React.useState(false);
  const [done, setDone] = React.useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contacts`, form);
      toast.success("Message sent. We'll respond within 1 business day.");
      setDone(true);
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong.";
      toast.error(Array.isArray(msg) ? msg[0]?.msg || "Validation error" : msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <SiteLayout>
      <section className="section-pad" data-testid="contact-hero">
        <div className="max-w-6xl mx-auto grid md:grid-cols-12 gap-12 items-start">
          <div className="md:col-span-5">
            <div className="eyebrow">Contact</div>
            <h1 className="font-serif text-4xl md:text-5xl mt-5 leading-tight">
              We’d love to <em className="not-italic text-[var(--evara-primary)] font-medium">hear from you.</em>
            </h1>
            <p className="mt-6 text-[var(--evara-text-2)] leading-relaxed">
              For partnership enquiries, capability building programs, media or general questions, drop us a note. We respond within one business day.
            </p>
            <div className="mt-10 space-y-5 text-sm">
              <div className="flex items-start gap-3">
                <Mail size={18} className="text-[var(--evara-primary)] mt-0.5" />
                <div>
                  <div className="eyebrow">Email</div>
                  <a href={`mailto:${EVARA.contact.email}`} className="hairline-link text-base" data-testid="contact-email">
                    {EVARA.contact.email}
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Phone size={18} className="text-[var(--evara-primary)] mt-0.5" />
                <div>
                  <div className="eyebrow">Phone</div>
                  <a href={`tel:${EVARA.contact.phone.replace(/\s+/g, "")}`} className="hairline-link text-base" data-testid="contact-phone">
                    {EVARA.contact.phone}
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <MapPin size={18} className="text-[var(--evara-primary)] mt-0.5" />
                <div>
                  <div className="eyebrow">Office</div>
                  <div className="text-base" data-testid="contact-address">{EVARA.contact.address}</div>
                </div>
              </div>
            </div>
          </div>

          <form onSubmit={submit} className="md:col-span-7 card-evara p-7 md:p-9 space-y-5" data-testid="contact-form">
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label className="evara-label">Name</label>
                <input required className="evara-input" value={form.name} onChange={onChange("name")} placeholder="Your name" data-testid="contact-form-name" />
              </div>
              <div>
                <label className="evara-label">Email</label>
                <input required type="email" className="evara-input" value={form.email} onChange={onChange("email")} placeholder="you@email.com" data-testid="contact-form-email" />
              </div>
            </div>
            <div>
              <label className="evara-label">Subject</label>
              <input className="evara-input" value={form.subject} onChange={onChange("subject")} placeholder="How can we help?" data-testid="contact-form-subject" />
            </div>
            <div>
              <label className="evara-label">Message</label>
              <textarea required rows={6} className="evara-input" value={form.message} onChange={onChange("message")} placeholder="A few lines about what you’re looking for…" data-testid="contact-form-message" />
            </div>
            <div className="flex items-center justify-between pt-2">
              {done ? (
                <span className="text-xs text-[var(--evara-primary)]">✓ Message sent. Thank you.</span>
              ) : (
                <span className="text-xs text-[var(--evara-muted)]">We respond within 1 business day.</span>
              )}
              <button type="submit" className="btn-primary" disabled={loading} data-testid="contact-form-submit">
                {loading ? "Sending…" : "Send message"} {!loading && <ArrowRight size={16} />}
              </button>
            </div>
          </form>
        </div>
      </section>
    </SiteLayout>
  );
}
